import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Header, Button } from '../../components/mte/shell';
import { apiFetch, ApiError } from '../../lib/auth-fetch';
import type { HeroId } from '../../components/mte/placeholders';
import styles from './EspaceParent.module.css';

interface Child { childId: string; firstName: string; schoolLevel: 'CP'|'CE1'|'CE2'|'CM1'|'CM2'; heroId: HeroId | null; archivedAt?: string | null; }

type ConfirmAction =
  | { kind: 'archive'; childId: string; firstName: string }
  | { kind: 'restore'; childId: string; firstName: string }
  | { kind: 'delete'; childId: string; firstName: string }
  | null;

function EspaceParent(): JSX.Element {
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction>(null);
  const [showArchived, setShowArchived] = useState(false);
  
  const [editing, setEditing] = useState<{ childId: string; firstName: string; schoolLevel: Child['schoolLevel'] } | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [newChildFirstName, setNewChildFirstName] = useState<string>('');
  const [newChildLevel, setNewChildLevel] = useState<Child['schoolLevel']>('CE1');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);

  async function refresh() {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch<{ children: Child[] }>(
        showArchived ? '/api/parent/children?includeArchived=1' : '/api/parent/children'
      );
      setChildren(data?.children ?? []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Impossible de charger les profils.');
      setChildren([]);
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void refresh(); }, [showArchived]);

  async function archiveChild(childId: string) {
    try { await apiFetch('/api/parent/children/' + encodeURIComponent(childId), { method: 'DELETE' }); await refresh(); }
    catch (e) { alert('Erreur: ' + (e instanceof Error ? e.message : String(e))); }
  }
  async function restoreChild(childId: string) {
    try { await apiFetch('/api/parent/children/' + encodeURIComponent(childId), { method: 'PATCH', body: JSON.stringify({ archivedAt: null }) }); await refresh(); }
    catch (e) { alert('Erreur: ' + (e instanceof Error ? e.message : String(e))); }
  }
  async function deleteChild(childId: string) {
    try { await apiFetch('/api/parent/children/' + encodeURIComponent(childId), { method: 'PATCH', body: JSON.stringify({ permanentDelete: true }) }); await refresh(); }
    catch (e) { alert('Erreur: ' + (e instanceof Error ? e.message : String(e))); }
  }
  async function saveEdit() {
    if (!editing) return;
    try {
      await apiFetch('/api/parent/children/' + encodeURIComponent(editing.childId), { method: 'PATCH', body: JSON.stringify({ firstName: editing.firstName, schoolLevel: editing.schoolLevel }) });
      setEditing(null); await refresh();
    } catch (e) { alert('Erreur: ' + (e instanceof Error ? e.message : String(e))); }
  }
  function checkDuplicateThenCreate(firstName: string, level?: Child['schoolLevel']) {
    const norm = firstName.trim().toLowerCase();
    if (!norm) return;
    const existing = children.find((c) => c.firstName.trim().toLowerCase() === norm && !c.archivedAt);
    if (existing) {
      setDuplicateWarning('Un profil portant ce prénom existe déjà. Continuer quand même ?');
      return;
    }
    void createChild(firstName, level);
  }

  async function createChild(firstName: string, level?: Child['schoolLevel']) {
    try {
      await apiFetch('/api/parent/children', {
        method: 'POST',
        body: JSON.stringify({ firstName, schoolLevel: level || newChildLevel, heroId: null }),
      });
      await refresh();
      setShowAddForm(false);
      setNewChildFirstName('');
    } catch (e) {
      window.alert('Erreur: ' + (e instanceof Error ? e.message : 'Création échouée'));
    }
  }

  return (
    <Layout
      context="adulte"
      width="narrow"
      header={
        <Header
          context="adulte"
          logo={
            <>
              <span className={styles.brandMark} aria-hidden="true">✦</span>
              <span className={styles.brandText}>Mon Temps d'Étude</span>
            </>
          }
        />
      }
      footer={
        <footer className={styles.footer}>
          <p>
            <Link to="/famille/accueil/">← Quitter l'espace parent</Link>
          </p>
        </footer>
      }
    >
      <main className={styles.main}>
        <p className={styles.eyebrow}>Espace Parent</p>
        <h1 className={styles.title}>Mes enfants</h1>
        <p className={styles.lede}>Chaque enfant a son propre profil. Les progressions sont strictement separees.</p>

        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.addBtn}
            data-action="open-add"
            onClick={() => setShowAddForm(true)}
          >+ Ajouter un enfant</button>
          <label className={styles.toggleLabel}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="toggle-archived" />
            Afficher les profils archives
          </label>
        </div>

        {loading && <p>Chargement...</p>}
        {error && <p className={styles.errorMsg}>{error}</p>}

        {!loading && !error && children.length === 0 && (
          <p className={styles.emptyMsg}>Aucun profil. Utilisez le bouton ci-dessus pour creer le premier.</p>
        )}

        {!loading && !error && children.length > 0 && (
          <ul className={styles.list} role="list">
            {children.map((c) => {
              const isArchived = !!c.archivedAt;
              return (
                <li key={c.childId} className={isArchived ? styles.item + ' ' + styles.itemArchived : styles.item} data-child-id={c.childId}>
                  <div className={styles.itemHeader}>
                    <span className={styles.itemName}>{c.firstName}</span>
                    <span className={styles.itemLevel}>{c.schoolLevel}</span>
                    {isArchived && <span className={styles.archivedBadge}>archive</span>}
                  </div>
                  {editing?.childId === c.childId ? (
                    <div className={styles.editForm}>
                      <label>Prenom<input type="text" value={editing.firstName} maxLength={24} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} data-testid="edit-firstname" /></label>
                      <label>Niveau<select value={editing.schoolLevel} onChange={(e) => setEditing({ ...editing, schoolLevel: e.target.value as Child['schoolLevel'] })} data-testid="edit-level">
                        {(['CP','CE1','CE2','CM1','CM2'] as const).map((l) => <option key={l} value={l}>{l}</option>)}
                      </select></label>
                      <div className={styles.editActions}>
                        <Button tone="primary" size="sm" context="adulte" onClick={saveEdit} data-action="save-edit">Enregistrer</Button>
                        <Button tone="ghost" size="sm" context="adulte" onClick={() => setEditing(null)} data-action="cancel-edit">Annuler</Button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.actions}>
                      {!isArchived && (
                        <>
                          <Button tone="ghost" size="sm" context="adulte" onClick={() => setEditing({ childId: c.childId, firstName: c.firstName, schoolLevel: c.schoolLevel })} data-action="edit">Modifier</Button>
                          <Button tone="ghost" size="sm" context="adulte" onClick={() => setConfirm({ kind: 'archive', childId: c.childId, firstName: c.firstName })} data-action="archive">Archiver</Button>
                        </>
                      )}
                      {isArchived && (
                        <Button tone="ghost" size="sm" context="adulte" onClick={() => setConfirm({ kind: 'restore', childId: c.childId, firstName: c.firstName })} data-action="restore">Restaurer</Button>
                      )}
                      <Button tone="danger" size="sm" context="adulte" onClick={() => setConfirm({ kind: 'delete', childId: c.childId, firstName: c.firstName })} data-action="delete">Supprimer definitivement</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {confirm && (
          <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
            <div className={styles.confirmCard}>
              {confirm.kind === 'archive' && (<><h2>Archiver le profil de {confirm.firstName} ?</h2><p>Le profil disparait de la liste principale. Vous pouvez le restaurer plus tard.</p></>)}
              {confirm.kind === 'restore' && (<><h2>Restaurer le profil de {confirm.firstName} ?</h2><p>Le profil reapparait dans la liste principale.</p></>)}
              {confirm.kind === 'delete' && (<><h2>Supprimer definitivement ce profil ?</h2><p>Cette action supprimera les donnees et la progression associees a {confirm.firstName}.</p></>)}
              <div className={styles.confirmActions}>
                <Button tone="ghost" size="md" context="adulte" onClick={() => setConfirm(null)} data-action="cancel-confirm">Annuler</Button>
                <Button tone={confirm.kind === 'delete' ? 'danger' : 'primary'} size="md" context="adulte"
                  onClick={async () => {
                    const action = confirm; setConfirm(null);
                    if (action.kind === 'archive') await archiveChild(action.childId);
                    if (action.kind === 'restore') await restoreChild(action.childId);
                    if (action.kind === 'delete') await deleteChild(action.childId);
                  }} data-action="confirm-action">Confirmer</Button>
              </div>
            </div>
          </div>
        )}

        {showAddForm && (
          <div className={styles.addForm} data-testid="add-child-form">
            <h3>Ajouter un enfant</h3>
            <label>
              Prénom
              <input
                type="text"
                maxLength={24}
                value={newChildFirstName}
                onChange={(e) => setNewChildFirstName(e.target.value)}
                data-testid="add-child-firstname"
              />
            </label>
            <label>
              Niveau scolaire
              <select
                value={newChildLevel}
                onChange={(e) => setNewChildLevel(e.target.value as Child['schoolLevel'])}
                data-testid="add-child-level"
              >
                <option value="CP">CP</option>
                <option value="CE1">CE1</option>
                <option value="CE2">CE2</option>
                <option value="CM1">CM1</option>
                <option value="CM2">CM2</option>
              </select>
            </label>
            <div className={styles.addFormActions}>
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={() => { setShowAddForm(false); setNewChildFirstName(''); }}
                data-action="cancel-add"
              >
                Annuler
              </button>
              <button
                type="button"
                className={styles.primaryAction}
                onClick={() => checkDuplicateThenCreate(newChildFirstName, newChildLevel)}
                disabled={!newChildFirstName.trim()}
                data-action="confirm-add"
              >
                Créer le profil
              </button>
            </div>
          </div>
        )}
                {duplicateWarning && (
          <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
            <div className={styles.confirmCard}>
              <h2>Doublon detecte</h2>
              <p>{duplicateWarning}</p>
              <div className={styles.confirmActions}>
                <Button tone="ghost" size="md" context="adulte" onClick={() => setDuplicateWarning(null)} data-action="cancel-duplicate">Annuler</Button>
                <Button tone="primary" size="md" context="adulte" onClick={() => { setDuplicateWarning(null); void createChild(newChildFirstName, newChildLevel); }} data-action="continue-duplicate">Continuer quand même</Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
export default EspaceParent;
